import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import LinearProgress from '@mui/material/LinearProgress';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { appendModules } from '@platform/client';
import { COLLECTION_NOUN } from '@platform/schema/content';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { useTrackedMutation } from '../actions/ActionsProvider';
import { ChatPanel } from '../authoring/ChatPanel';
import { DragList } from '../ordering/DragList';
import { moved } from '../ordering/moved';
import { SCREEN_GAP } from '../routes/PageHeader';

import { collectionQuery } from './collection';
import { CollectionHeader } from './CollectionHeader';
import { CollectionSettings } from './CollectionSettings';
import { ModulePicker } from './ModulePicker';
import {
  pathChanged,
  pathModulesKey,
  pathModulesQuery,
  placePathModules,
} from './path-modules';
import { PublishPanel } from './PublishPanel';
import { STATUS_COLOUR } from './status';

import type { PathModule } from './path-modules';
import type { MouseEvent } from 'react';

/**
 * One path, and the sequence it claims — GEN-59, and PATH-02.
 *
 * A path is an ordered list of modules, never of lessons. Modules reorder here; lessons
 * keep each module's own order and are shown, read-only, under their module.
 *
 * The planner sits beside it — PATH-01 — and is offered the modules with their lessons.
 */
export function PathDetail() {
  const { id = '' } = useParams<{ id: string }>();
  const queries = useQueryClient();
  // A new key remounts the picker empty once its module has been added.
  const [picks, setPicks] = useState(0);

  const path = useQuery(collectionQuery(id));
  const modules = useQuery(pathModulesQuery(id));

  const row = path.data?.data;
  const held = modules.data?.data.modules ?? [];
  const published = row?.status === 'PUBLISHED';

  /** Written into the cache first, so a dropped row does not snap back for a round trip. */
  const place = async (next: PathModule[]): Promise<void> => {
    queries.setQueryData(pathModulesKey(id), { data: { modules: next } });
    await placePathModules(
      id,
      next.map((one) => one.id),
    );
  };

  // Not `pathChanged`: no publish rule sees position, so readiness is not asked again.
  const reorder = useTrackedMutation('Reorder the path', {
    mutationFn: place,
    onSettled: () =>
      Promise.all([
        queries.invalidateQueries({ queryKey: pathModulesKey(id) }),
        queries.invalidateQueries({ queryKey: ['collection'] }),
      ]),
  });

  const remove = useTrackedMutation('Take a module out of the path', {
    mutationFn: place,
    onSettled: () => pathChanged(queries, id),
  });

  const add = useTrackedMutation('Add a module to the path', {
    mutationFn: async (moduleId: string) => {
      await appendModules({
        path: { id },
        body: { moduleIds: [moduleId] },
        throwOnError: true,
      });
    },
    onSuccess: async () => {
      setPicks((count) => count + 1);
      await pathChanged(queries, id);
    },
  });

  const frozen = published || reorder.isPending || remove.isPending;
  const live = held.filter((one) => one.status === 'PUBLISHED');

  if (path.isPending) return <LinearProgress />;
  if (row === undefined)
    return <Alert severity="error">No path with that id.</Alert>;

  // A button inside the summary would otherwise open and close the row as well.
  const only =
    (act: () => void) =>
    (event: MouseEvent): void => {
      event.stopPropagation();
      act();
    };

  return (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      spacing={SCREEN_GAP}
      sx={{ alignItems: 'flex-start' }}
    >
      <Stack spacing={SCREEN_GAP} sx={{ flex: 1, maxWidth: 820, minWidth: 0 }}>
        <CollectionHeader row={row} />
        <Paper variant="outlined" sx={{ p: 2 }}>
          <CollectionSettings row={row} />
        </Paper>

        {published && (
          <Alert severity="info">
            You can add modules to this published path. New modules go to the
            end. To reorder or remove modules, move the path back to draft under
            Publishing. Lesson order comes from each module and can&apos;t be
            changed here.
          </Alert>
        )}

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={2}>
            <Typography variant="h6">The sequence</Typography>

            {modules.isPending && <LinearProgress />}

            {modules.isSuccess && held.length === 0 && (
              <Alert severity="warning">
                This path holds no modules, so there is nothing to put a learner
                on. Add one below.
              </Alert>
            )}

            {published && held.length > 0 && live.length === 0 && (
              <Alert severity="warning">
                This published path has no published modules, so learners see
                nothing in it.
              </Alert>
            )}

            {published && live.length > 0 && live.length < held.length && (
              <Alert severity="warning">
                This published path holds a draft module. Learners don&apos;t
                see it until it is published.
              </Alert>
            )}

            {held.length > 0 && (
              <DragList
                disabled={frozen}
                onMove={(from, to) => {
                  reorder.mutate(moved(held, from, to));
                }}
              >
                {held.map((one, index) => (
                  <Accordion
                    key={one.id}
                    variant="outlined"
                    disableGutters
                    slotProps={{ transition: { unmountOnExit: true } }}
                  >
                    <AccordionSummary
                      aria-controls={`module-${one.id}-lessons`}
                      id={`module-${one.id}`}
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        sx={{ alignItems: 'center', flex: 1, minWidth: 0 }}
                      >
                        <Typography variant="body2" sx={{ flex: 1 }}>
                          {index + 1}.{' '}
                          <Link
                            to={`/library/modules/${one.id}`}
                            onClick={(event) => {
                              event.stopPropagation();
                            }}
                          >
                            {one.title}
                          </Link>
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {one.courseTitle ?? 'No course'}
                        </Typography>
                        <Chip
                          size="small"
                          color={STATUS_COLOUR[one.status]}
                          label={one.status}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {String(one.lessons.length)} lesson(s)
                        </Typography>
                        <Button
                          size="small"
                          aria-label={`Move ${one.title} up`}
                          disabled={frozen || index === 0}
                          onClick={only(() => {
                            reorder.mutate(moved(held, index, index - 1));
                          })}
                        >
                          Up
                        </Button>
                        <Button
                          size="small"
                          aria-label={`Move ${one.title} down`}
                          disabled={frozen || index === held.length - 1}
                          onClick={only(() => {
                            reorder.mutate(moved(held, index, index + 1));
                          })}
                        >
                          Down
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          aria-label={`Remove ${one.title}`}
                          disabled={frozen}
                          onClick={only(() => {
                            remove.mutate(
                              held.filter((other) => other.id !== one.id),
                            );
                          })}
                        >
                          Remove
                        </Button>
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails id={`module-${one.id}-lessons`}>
                      {one.lessons.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          This module holds no lessons yet.
                        </Typography>
                      ) : (
                        <List dense disablePadding>
                          {one.lessons.map((lesson) => (
                            <ListItem key={lesson.id} disableGutters>
                              <ListItemText
                                primary={
                                  <>
                                    {String(lesson.position)}.{' '}
                                    <Link
                                      to={`/authoring/lessons/${lesson.id}`}
                                    >
                                      {lesson.title}
                                    </Link>
                                  </>
                                }
                                secondary={lesson.status}
                              />
                            </ListItem>
                          ))}
                        </List>
                      )}
                    </AccordionDetails>
                  </Accordion>
                ))}
              </DragList>
            )}
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={1}>
            <Typography variant="h6">Add modules</Typography>
            <ModulePicker
              key={picks}
              width={420}
              label="Find a module"
              detailed
              hide={new Set(held.map((one) => one.id))}
              onChange={(module) => {
                if (module) add.mutate(module.id);
              }}
            />
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={2}>
            <Typography variant="h6">Publishing</Typography>
            <PublishPanel id={id} />
          </Stack>
        </Paper>
      </Stack>

      {/* No `onApply`: a collection carries no proposal —
          `docs/decisions/path-chat-proposes-nothing.md`. */}
      <ChatPanel
        subject={{
          kind: 'collection',
          collectionId: id,
          noun: COLLECTION_NOUN[row.kind],
        }}
      />
    </Stack>
  );
}
